import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { useToast } from "~/hooks/use-toast";
import {
  convertToSalesRequest,
  deleteQuotation,
  duplicateQuotation,
  expireOldQuotations,
  getQuotationById,
  getQuotations,
  getQuotationsByClient,
  markQuotationAsAccepted,
  markQuotationAsRejected,
  markQuotationAsSent,
} from "~/services/quotationService";
import { useProfileStore } from "~/store/profileStore";
import type { QuotationStatus, QuotationWithDetails } from "~/types/quotation";

interface UseQuotationsOptions {
  clientId?: string;
  status?: QuotationStatus;
  createdBy?: string;
  fromDate?: string;
  toDate?: string;
}

const QUOTATIONS_QUERY_KEY = "quotations";

/**
 * Hook to fetch and manage quotations
 */
export const useQuotations = (options?: UseQuotationsOptions) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { id: profileId } = useProfileStore();

  // Fetch quotations with optional filters
  const {
    data: quotations = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [QUOTATIONS_QUERY_KEY, options],
    queryFn: async () => {
      // First, expire old quotations
      await expireOldQuotations().catch(console.error);

      // Then fetch with filters
      return getQuotations(options);
    },
    staleTime: 30 * 1000, // 30 seconds
  });

  // Invalidate cache
  const invalidateQuotations = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QUOTATIONS_QUERY_KEY] });
  }, [queryClient]);

  // Mark as sent
  const handleMarkAsSent = useCallback(
    async (quotationId: string) => {
      try {
        await markQuotationAsSent(quotationId);
        toast({
          title: "Estado actualizado",
          description: "El presupuesto ha sido marcado como enviado",
        });
        invalidateQuotations();
      } catch (error) {
        console.error("Error marking quotation as sent:", error);
        toast({
          title: "Error",
          description: "No se pudo actualizar el estado",
          variant: "destructive",
        });
      }
    },
    [toast, invalidateQuotations]
  );

  // Mark as accepted
  const handleMarkAsAccepted = useCallback(
    async (quotationId: string) => {
      try {
        await markQuotationAsAccepted(quotationId);
        toast({
          title: "Estado actualizado",
          description: "El presupuesto ha sido marcado como aceptado",
        });
        invalidateQuotations();
      } catch (error) {
        console.error("Error marking quotation as accepted:", error);
        toast({
          title: "Error",
          description: "No se pudo actualizar el estado",
          variant: "destructive",
        });
      }
    },
    [toast, invalidateQuotations]
  );

  // Mark as rejected
  const handleMarkAsRejected = useCallback(
    async (quotationId: string, reason?: string) => {
      try {
        await markQuotationAsRejected(quotationId, reason);
        toast({
          title: "Estado actualizado",
          description: "El presupuesto ha sido marcado como rechazado",
        });
        invalidateQuotations();
      } catch (error) {
        console.error("Error marking quotation as rejected:", error);
        toast({
          title: "Error",
          description: "No se pudo actualizar el estado",
          variant: "destructive",
        });
      }
    },
    [toast, invalidateQuotations]
  );

  // Delete quotation
  const handleDelete = useCallback(
    async (quotationId: string) => {
      try {
        await deleteQuotation(quotationId);
        toast({
          title: "Presupuesto eliminado",
          description: "El presupuesto ha sido eliminado",
        });
        invalidateQuotations();
      } catch (error) {
        console.error("Error deleting quotation:", error);
        toast({
          title: "Error",
          description:
            error instanceof Error
              ? error.message
              : "No se pudo eliminar el presupuesto",
          variant: "destructive",
        });
      }
    },
    [toast, invalidateQuotations]
  );

  // Convert to sales request
  const handleConvertToSalesRequest = useCallback(
    async (quotationId: string): Promise<string | null> => {
      if (!profileId) {
        toast({
          title: "Error",
          description: "Debes iniciar sesión",
          variant: "destructive",
        });
        return null;
      }

      try {
        const salesRequestId = await convertToSalesRequest(quotationId, profileId);
        toast({
          title: "Conversión exitosa",
          description: "El presupuesto ha sido convertido a nota de venta",
        });
        invalidateQuotations();
        return salesRequestId;
      } catch (error) {
        console.error("Error converting quotation:", error);
        toast({
          title: "Error",
          description:
            error instanceof Error
              ? error.message
              : "No se pudo convertir el presupuesto",
          variant: "destructive",
        });
        return null;
      }
    },
    [profileId, toast, invalidateQuotations]
  );

  // Duplicate quotation
  const handleDuplicate = useCallback(
    async (quotationId: string): Promise<string | null> => {
      if (!profileId) {
        toast({
          title: "Error",
          description: "Debes iniciar sesión",
          variant: "destructive",
        });
        return null;
      }

      try {
        const newQuotation = await duplicateQuotation(quotationId, profileId);
        toast({
          title: "Presupuesto duplicado",
          description: `Se ha creado el presupuesto #${newQuotation.quotation_number}`,
        });
        invalidateQuotations();
        return newQuotation.id;
      } catch (error) {
        console.error("Error duplicating quotation:", error);
        toast({
          title: "Error",
          description: "No se pudo duplicar el presupuesto",
          variant: "destructive",
        });
        return null;
      }
    },
    [profileId, toast, invalidateQuotations]
  );

  return {
    quotations,
    isLoading,
    error,
    refetch,
    invalidateQuotations,
    handleMarkAsSent,
    handleMarkAsAccepted,
    handleMarkAsRejected,
    handleDelete,
    handleConvertToSalesRequest,
    handleDuplicate,
  };
};

/**
 * Hook to fetch quotations for a specific client
 */
export const useClientQuotations = (clientId: string) => {
  return useQuotations({ clientId });
};

/**
 * Hook to fetch a single quotation by ID
 */
export const useQuotation = (quotationId: string | undefined) => {
  return useQuery({
    queryKey: [QUOTATIONS_QUERY_KEY, quotationId],
    queryFn: () => (quotationId ? getQuotationById(quotationId) : null),
    enabled: !!quotationId,
    staleTime: 30 * 1000,
  });
};
